import type { Match, Team } from '@uefadigital/panenka';
import { MatchStatus } from '@uefadigital/panenka';
import { PkEmptyMessage, PkIdentifier } from '@uefadigital/panenka-react';
import i18n from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetRefereeMatchesByIdQuery } from 'api/fspApi';
import { translateEntity } from 'utils/translateEntity';

export interface RefereeResultsDate {
  refereeId: string | undefined;
  competitionId: string;
  isDesktop: boolean;
  isFemale: boolean;
}

export const RefereeResults = ({ refereeId, competitionId, isDesktop, isFemale }: RefereeResultsDate) => {
  const { t } = useTranslation();

  const {
    data: refereeMatchesData = [],
    isLoading: refereeMatchesLoading,
    isError: refereeMatchesError,
  } = useGetRefereeMatchesByIdQuery(
    { refereeId: refereeId!, competitionId },
    {
      skip: !refereeId,
    },
  );

  const sortedMatches = useMemo(
    () =>
      refereeMatchesData
        ?.toSorted(
          (match, nextMatch) =>
            new Date(nextMatch?.kickOffTime?.date ?? '').getTime() - new Date(match?.kickOffTime?.date ?? '').getTime(),
        )
        .filter((match) => match.status !== MatchStatus.UPCOMING),
    [refereeMatchesData],
  );

  const getExpandedIdentifier = useCallback(
    (primary: string, secondary: string) => {
      const primaryFont = isDesktop ? 'pk-font-size--l pr-font-size--14' : 'pk-font-size--m';
      const secondaryFont = isDesktop ? 'pk-font-size--m pr-font-size--12' : 'pk-font-size--m';

      return (
        <PkIdentifier alignment="left" className="pk-mb--m pr-pb--s">
          <span slot="primary" className={`pk-text--ui-06 ${primaryFont} pk-font-weight--bold pk-line-height--md`}>
            {primary}
          </span>
          <span
            slot="secondary"
            className={`pk-text--text-02 ${secondaryFont} pk-font-weight--normal pk-line-height--sm`}
          >
            {secondary}
          </span>
        </PkIdentifier>
      );
    },
    [isDesktop],
  );

  const displayTeamName = useCallback(
    (team: Team | undefined) => {
      if (translateEntity(team)?.displayName) {
        return translateEntity(team).displayName;
      }

      if (team?.teamCode) {
        return team.teamCode;
      }

      return t('team_uppercase');
    },
    [t],
  );

  const matchResults = useMemo(
    () =>
      sortedMatches?.map((match: Match) => {
        const homeTeam = displayTeamName(match?.homeTeam);
        const awayTeam = displayTeamName(match?.awayTeam);
        const score = `${match?.score?.total?.home} - ${match?.score?.total?.away}`;
        const competition = translateEntity(match?.competition)?.name;
        const start = new Date(match.kickOffTime?.dateTime ?? '');
        const date = start.toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const primary = `${homeTeam} ${score} ${awayTeam}`;
        const secondary = `${date} • ${competition}`;

        return getExpandedIdentifier(primary, secondary);
      }),
    [displayTeamName, getExpandedIdentifier, sortedMatches],
  );

  if (refereeId === undefined || refereeMatchesLoading || refereeMatchesError || !refereeMatchesData.length) {
    return <PkEmptyMessage>{t(`no_latest_main_referee_results_available${isFemale ? '_f' : ''}`)}</PkEmptyMessage>;
  }

  return <div>{matchResults}</div>;
};
